import Component from '../abstracts/Component';

export default class CollapseComponent extends Component {

  get COMPONENTNAME() {
    return 'CollapseComponent';
  }

  get SELECTORS() {
    return {
      trigger: '[data-trigger]',
      tab: '[data-tab]',
      button: '[data-collapsable-button]',
    };
  }

  get CLASSES() {
    return {
      collapsed: 'collapsed'
    }
  }

  get BREAKPOINTS() {
    return {
      large: 1024,
      medium: 768
    }
  }

  constructor(compEl) {
    super(compEl);
    this.isDesktop = false;
    this.isFooterComponent = this.checkIfFooterComponent();
    this.isServiceNavComponent = this.checkIfServiceNavComponent();
    this.collapseMaxRes = this.getCollapseMaxRes();
    this.intersectionObserver = null;
    this.scrollListeners = new Set();
  }

  checkIfFooterComponent() {
    return this.$component.closest('.c-footer') !== null;
  }

  checkIfServiceNavComponent() {
    return this.$component.closest('.service-navigation') !== null;
  }

  getCollapseMaxRes() {
    const maxRes = this.$component.dataset.collapseMaxres;
    if (maxRes === 'md') return 'medium';
    if (maxRes === 'lg') return 'large';
    return null;
  }

  readDOM() {
    return {
      triggers: this.$component.querySelectorAll(this.SELECTORS.trigger),
      tabs: this.$component.querySelectorAll(this.SELECTORS.tab),
      currentOpenTab: null,
    };
  }

  init() {
    this.checkScreenSize();

    if(this.dom.tabs) {
      this.dom.tabs.forEach(tab => {
        tab.style.height = tab.offsetHeight + "px";
      });
    }

    if(this.dom.triggers) {
      this.dom.triggers.forEach(trigger => {
        if(trigger.dataset.trigger === "start-open") {
          trigger.classList.remove(this.CLASSES.collapsed);
          trigger.querySelector(this.SELECTORS.button)?.setAttribute("aria-expanded", "true");
          this.dom.currentOpenTab = trigger;
          this.setTabVisibility(trigger, true);

          const tabContent = trigger.parentNode.querySelector(this.SELECTORS.tab);
          if (tabContent) {
            tabContent.style.height = tabContent.scrollHeight + "px";
          }
        } else {
        // for desktop footer, always expanded
          const shouldBeExpanded = this.isFooterComponent && this.isDesktop;
          
          if (shouldBeExpanded) {
            trigger.classList.remove(this.CLASSES.collapsed);
            trigger.querySelector(this.SELECTORS.button)?.setAttribute("aria-expanded", "true");
            this.setTabVisibility(trigger, true);
            
            const tabContent = trigger.parentNode.querySelector(this.SELECTORS.tab);
            if (tabContent) {
              tabContent.style.height = tabContent.scrollHeight + "px";
            }
          } else {
            trigger.classList.add(this.CLASSES.collapsed);
            trigger.querySelector(this.SELECTORS.button)?.setAttribute("aria-expanded", "false");
            this.setTabVisibility(trigger, false);
          }
        }

        // Disable click
        const shouldDisableClick = (this.isFooterComponent && this.isDesktop) ||
                                  (this.isServiceNavComponent && this.collapseMaxRes === 'medium' && this.isDesktop);

        if (shouldDisableClick) {
          this.disableTriggerClick(trigger);
        } else {
          this.enableTriggerClick(trigger);
        }
      });
    }
  }

  checkScreenSize() {
    this.isDesktop = window.innerWidth >= this.BREAKPOINTS.large;
    this.isMediumOrLarger = window.innerWidth >= this.BREAKPOINTS.medium;
  }

  shouldComponentCollapse() {
    if (this.isFooterComponent || (this.isServiceNavComponent && this.collapseMaxRes === 'medium')) {
      return !this.isDesktop;
    }
    return true;
  }

  disableTriggerClick(trigger) {
    trigger.style.cursor = 'default';
    trigger.style.pointerEvents = 'none';
    const links = trigger.querySelectorAll('a');
    links.forEach(link => {
      link.style.pointerEvents = 'auto';
    });
  }

  enableTriggerClick(trigger) {
    trigger.style.cursor = '';
    trigger.style.pointerEvents = '';
    const links = trigger.querySelectorAll('a');
    links.forEach(link => {
      link.style.pointerEvents = '';
    });
  }

  scrollToAccordionHeader(trigger) {
    const scrollContainer = trigger.closest('.pdp__info');
    if (!scrollContainer) {
      return;
    }

    const headerOffset = 60;
    let targetPosition;

    if (window.innerWidth < this.BREAKPOINTS.large) {
      const triggerRect = trigger.getBoundingClientRect();
      targetPosition = triggerRect.top + window.pageYOffset - headerOffset;
    } else {
      const containerRect = scrollContainer.getBoundingClientRect();
      const targetTop = Math.max(headerOffset, (window.innerHeight - containerRect.height) / 2);
      targetPosition = window.pageYOffset + containerRect.top - targetTop;
    }

    this.performScroll(window, targetPosition, () => this.scrollInsideContainer(trigger, scrollContainer));
  }

  scrollInsideContainer(trigger, scrollContainer) {
    const containerRect = scrollContainer.getBoundingClientRect();
    const headerRect = trigger.getBoundingClientRect();
    const headerRelativeTop = headerRect.top - containerRect.top;
    const isHeaderVisible = headerRelativeTop >= 20 && headerRelativeTop <= containerRect.height * 0.3;

    if (!isHeaderVisible) {
      const targetScrollTop = Math.max(0, scrollContainer.scrollTop + headerRelativeTop - 20);
      this.performScroll(scrollContainer, targetScrollTop, () => this.finishScrollOperation());
    } else {
      this.finishScrollOperation();
    }
  }

  performScroll(element, targetPos, onComplete) {
    this.cleanupScrollListeners();
    const startTime = Date.now();
    const isWindow = element === window;

    const interrupt = () => { this.cleanupScrollListeners(); this.finishScrollOperation(); };
    const complete = () => { this.cleanupScrollListeners(); onComplete(); };

    const checkPosition = () => {
      const current = isWindow ? window.pageYOffset : element.scrollTop;
      if (Math.abs(current - targetPos) <= 4 || Date.now() - startTime >= 1500) {
        complete();
      } else {
        requestAnimationFrame(checkPosition);
      }
    };

    this.addScrollListener(element, 'wheel', interrupt, { passive: true });
    this.addScrollListener(element, 'touchmove', interrupt, { passive: true });

    if ('onscrollend' in element) {
      this.addScrollListener(element, 'scrollend', complete);
    } else {
      requestAnimationFrame(checkPosition);
    }

    element.scrollTo({ top: targetPos, behavior: 'smooth' });
  }


  addScrollListener(element, event, handler, options = {}) {
    const listenerInfo = { element, event, handler, options };
    this.scrollListeners.add(listenerInfo);
    element.addEventListener(event, handler, options);
  }

  cleanupScrollListeners() {
    this.scrollListeners.forEach(({ element, event, handler, options }) => {
      element.removeEventListener(event, handler, options);
    });
    this.scrollListeners.clear();
  }

  finishScrollOperation() {
    this.cleanupScrollListeners();
  }

  closeCurrentAccordion() {
    if (this.dom.currentOpenTab) {
      this.dom.currentOpenTab.classList.add(this.CLASSES.collapsed);
      this.dom.currentOpenTab.querySelector(this.SELECTORS.button)?.setAttribute("aria-expanded", "false");
      this.setTabVisibility(this.dom.currentOpenTab, false);
      this.dom.currentOpenTab = null;
    }
  }

  handleCollapse() {
    if(this.dom.triggers) {
      this.dom.triggers.forEach(trigger => {
        trigger.addEventListener("click", () => {
          if(this.dom.currentOpenTab === trigger) {
            trigger.classList.add(this.CLASSES.collapsed);
            trigger.querySelector(this.SELECTORS.button)?.setAttribute("aria-expanded","false");
            this.setTabVisibility(trigger, false);
            this.dom.currentOpenTab = null;
          } else {
            if(this.dom.currentOpenTab != null) {
              this.dom.currentOpenTab.classList.add(this.CLASSES.collapsed);
              this.dom.currentOpenTab.querySelector(this.SELECTORS.button)?.setAttribute("aria-expanded","false");
              this.setTabVisibility(this.dom.currentOpenTab, false);
            }

            const shouldScroll = this.shouldScrollToAccordion(trigger);

            trigger.classList.remove(this.CLASSES.collapsed);
            trigger.querySelector(this.SELECTORS.button)?.setAttribute("aria-expanded","true");
            this.setTabVisibility(trigger, true);
            this.dom.currentOpenTab = trigger;

            if (shouldScroll) {
              this.scrollToAccordionHeader(trigger);
            }
          }
        });
      });
    }
  }

  shouldScrollToAccordion(trigger) {
    const isPdpDetails = trigger.closest('.pdp__details') !== null;
    if (!isPdpDetails) return false;

    const scrollContainer = trigger.closest('.pdp__info');
    if (!scrollContainer) return false;

    const containerRect = scrollContainer.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const headerOffset = 60;
    const bottomMargin = 100;

    const containerTop = containerRect.top;
    const containerBottom = containerRect.bottom;
    const availableViewport = viewportHeight - headerOffset - bottomMargin;

    return containerTop < headerOffset ||
           containerBottom > viewportHeight - bottomMargin ||
           containerRect.height > availableViewport * 0.8;
  }

  setTabVisibility(trigger, isVisible) {
    const tabContent = trigger.parentNode.querySelector(this.SELECTORS.tab);
    if (tabContent) {
      const isSplashCountrySelector = tabContent.closest('.s-p__continent');
      if (!isSplashCountrySelector) {
        tabContent.style.visibility = isVisible ? 'visible' : 'hidden';
      }
    }
  }

  bindEvents() {
    this.handleCollapse();
    window.addEventListener('resize', () => {
      const wasDesktop = this.isDesktop;
      this.checkScreenSize();
      const shouldReinit = (wasDesktop !== this.isDesktop && this.isFooterComponent) ||
                          (wasDesktop !== this.isDesktop && this.isServiceNavComponent);

      if (shouldReinit) {
        this.init();
      }

      if (this.dom.currentOpenTab) {
        const tab = this.dom.currentOpenTab.parentNode.querySelector(this.SELECTORS.tab);
        tab.style.maxHeight = tab.scrollHeight + "px";
      }
    });
  }

  render() {
    super.render();
    this.log('Rendering...');
    this.dom = this.readDOM();
    this.init();
    this.bindEvents();
  }
}
